import { useAtomValue } from 'jotai';
import { Message } from 'primereact/message';
import React from 'react';
import {
    BanknoteIcon,
    ChartBar,
    CitrusIcon,
    H6Emphasized,
    HomeIcon,
    LayersIcon,
    ShieldUserIcon,
    SparklesIcon,
    SplitIcon,
    UserIcon,
    XStack,
} from 'smoothie';

import { SignOutButton } from '../../auth/components/SignoutButton';
import { adminUserAtom } from '../../entities/entityAtoms';
import { type AdminUser } from '../../entities/entityTypes';
import { MessagingButton } from '../../guide-messaging/components/MessagingButton';
import { SmallTileButton } from './GroveButtons';
import { GroveLogo } from './GroveLogo';
import { QuickSearchButton } from './QuickSearchModal';

// Used to help with dynamic window size calculations
export const APP_HEADER_HEIGHT = 64;

/**
 * @typedef {object} ref React Navigation Stack.Screen NativeStackNavigationOptions
 * @see https://reactnavigation.org/docs/screen
 */
export const BaseHeaderProps = {
    headerShown: true,
    headerStyle: {
        backgroundColor: 'white',
    },
    headerShadowVisible: true,
    headerBackTitleVisible: false,
    headerBackVisible: true,
    headerTitle: ({ children }: { children: string }) => {
        return <HeaderTitle title={children} />;
    },
};

const OooMessage = ({ adminUser }: { adminUser: AdminUser }) => {
    const oooStart = adminUser.guide?.oooStart;
    const oooEnd = adminUser.guide?.oooEnd;

    const oooDates = `${new Date(oooStart ?? Date.now()).toDateString()} - ${new Date(oooEnd ?? Date.now()).toDateString()}`;

    return adminUser.guide?.oooStart != null ? (
        <XStack>
            <Message
                style={{
                    width: '100%',
                }}
                text={`Out of office ${oooDates}`}
                severity="warn"
            />
        </XStack>
    ) : null;
};

export const HeaderTitle = ({ title }: { title: string }) => {
    const adminUser = useAtomValue(adminUserAtom);
    return (
        <XStack gap={32} alignItems="center">
            <GroveLogo />
            <H6Emphasized>{title}</H6Emphasized>
            <OooMessage adminUser={adminUser} />
        </XStack>
    );
};

export const HeaderActions = () => {
    const adminUser = useAtomValue(adminUserAtom);
    const canUseGuideMessaging = adminUser.authorization.guideMessaging.canUse;
    const canUseBankOpsFunctionality =
        adminUser.authorization.bankOpsFunctionality.canUse;
    const canUseImOpsFunctionality =
        adminUser.authorization.imOpsFunctionality.canUse;
    const canUseBulkIncomeSplitSimulation =
        adminUser.authorization.betaTransactionFeatures.canUse;
    const canAccessGuides = adminUser.authorization.guides.canViewAll;
    const canAccessAdminUsers = adminUser.authorization.adminUsers.canViewAll;

    const canUseWorkflows = adminUser.authorization.workflows.canUse;
    const canUseAiDashboard = adminUser.authorization.aiPrompts.canUse;

    return (
        <>
            <XStack
                paddingHorizontal={16}
                justifyContent="flex-end"
                alignItems="center"
                gap={4}
            >
                <QuickSearchButton />
                <SmallTileButton icon={HomeIcon} label="Home" href="/" />
                <SmallTileButton
                    icon={CitrusIcon}
                    label="Slices"
                    href="/fruit-slices"
                />
                {canUseGuideMessaging && <MessagingButton />}
                {canUseWorkflows && (
                    <SmallTileButton
                        icon={LayersIcon}
                        label="Workflows"
                        href="/workflows"
                    />
                )}
                {canUseAiDashboard && (
                    <SmallTileButton
                        icon={SparklesIcon}
                        label="AI"
                        href="/ai"
                    />
                )}
                {canUseBankOpsFunctionality && (
                    <SmallTileButton
                        icon={BanknoteIcon}
                        label="FF Ops"
                        href="/bank-ops"
                    />
                )}
                {canUseBulkIncomeSplitSimulation && (
                    <SmallTileButton
                        icon={SplitIcon}
                        iconRotation="180deg"
                        label="Simulator"
                        href="/income-split-bulk"
                    />
                )}
                {canUseImOpsFunctionality && (
                    <SmallTileButton
                        icon={ChartBar}
                        label="IM Ops"
                        href="/im-ops"
                    />
                )}
                {canAccessGuides && (
                    <SmallTileButton
                        icon={UserIcon}
                        label="Guides"
                        href="/guides"
                    />
                )}
                {canAccessAdminUsers && (
                    <SmallTileButton
                        icon={ShieldUserIcon}
                        label="Admins"
                        href="/admin-users"
                    />
                )}
                <SignOutButton />
            </XStack>
        </>
    );
};
